import { useCallback, useId, useRef, useState } from "react";
import { ApiError, type AdSession, type Api, type Me } from "../api";
import { FakeAd } from "../ads/FakeAd";
import { adsEnabled } from "../ads/enabled";
import { showRewardedAd, type AdOutcome } from "../ads/rewarded";
import { config } from "../config";
import { FREE_DAILY, errorMessage } from "../messages";
import { Modal } from "./Modal";

type Phase = "idle" | "disclose" | "loading" | "fake";

interface Props {
  api: Api;
  me: Me | null;
  /** Not signed in: the button opens the sign-in dialog instead (coins need an account). */
  guest: boolean;
  onNeedLogin: () => void;
  onCoins: () => void;
  emphasize: boolean;
}

export function AdOffer({ api, me, guest, onNeedLogin, onCoins, emphasize }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const session = useRef<AdSession | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const configured = adsEnabled();

  const fail = (code: string) => setMessage({ text: errorMessage(code), ok: false });

  const claim = useCallback(async () => {
    const s = session.current;
    if (!s) return;
    try {
      const res = await api.claimAd(s.session_id, s.nonce);
      setMessage({ text: res.already_granted ? "Token của quảng cáo này đã được cộng." : "Đã cộng 1 token. Cảm ơn bạn!", ok: true });
      onCoins();
    } catch (e) {
      fail(e instanceof ApiError ? e.code : "network");
    }
  }, [api, onCoins]);

  const finished = useCallback((outcome: AdOutcome) => {
    setPhase("idle");
    session.current = null;
    if (outcome === "unavailable") fail("ad_unavailable");
    if (outcome === "dismissed") setMessage({ text: "Bạn đã đóng quảng cáo trước khi nhận token.", ok: false });
  }, []);

  async function start() {
    setPhase("loading");
    setMessage(null);
    try {
      session.current = await api.openAd();
    } catch (e) {
      setPhase("idle");
      fail(e instanceof ApiError ? e.code : "network");
      return;
    }
    if (import.meta.env.DEV && config.fakeAds) { // compiled out of production builds
      setPhase("fake");
      return;
    }
    try {
      finished(await showRewardedAd(config.adUnitPath, () => void claim()));
    } catch {
      finished("unavailable"); // GPT blocked (ad blocker / network)
    }
  }

  const limitReached = me ? me.ads_left_today <= 0 : false;
  const walletFull = me ? me.earned >= me.earned_cap : false;
  const disabled = guest ? false : !me || limitReached || walletFull || phase !== "idle";
  if (!configured) return null;                     // no ad unit on this site: no button, no note

  return (
    <div className={`ad-offer${emphasize ? " is-emphasized" : ""}`}>
      <button type="button" className="btn btn-accent" disabled={disabled}
              onClick={() => (guest ? onNeedLogin() : setPhase("disclose"))}>
        {phase === "loading" ? "Đang tải quảng cáo…" : "Xem quảng cáo, nhận 1 token"}
      </button>
      <p className="hint">
        {guest ? `Đăng nhập để nhận ${FREE_DAILY} token miễn phí mỗi ngày và xem quảng cáo nhận thêm token.`
          : walletFull ? `Ví quảng cáo đã đầy (${me?.earned_cap} token).`
          : limitReached ? "Hôm nay bạn đã xem đủ số quảng cáo được thưởng."
          : `Còn ${me?.ads_left_today ?? "…"} lượt hôm nay. Token từ quảng cáo được giữ lại qua ngày.`}
      </p>
      {message && <p role={message.ok ? "status" : "alert"} className={message.ok ? "ok" : "error"}>{message.text}</p>}

      {phase === "disclose" && (
        <Modal titleId={titleId} onClose={() => setPhase("idle")} initialFocus={confirmRef}>
          <h2 id={titleId}>Nhận 1 token bằng quảng cáo</h2>
          <p>Xem một quảng cáo ngắn (thường 5–30 giây) để nhận <strong>1 token</strong>. Bạn có thể đóng quảng cáo bất
            cứ lúc nào nhưng sẽ không nhận token. Token chỉ dùng để tạo ảnh trên trang này, không đổi ra tiền.</p>
          <div className="actions">
            <button ref={confirmRef} type="button" className="btn btn-primary" onClick={() => void start()}>Xem quảng cáo</button>
            <button type="button" className="btn" onClick={() => setPhase("idle")}>Để sau</button>
          </div>
        </Modal>
      )}
      {import.meta.env.DEV && phase === "fake" && <FakeAd onGranted={() => void claim()} onDone={finished} />}
    </div>
  );
}
